/**
 * Copyright(c) Live2D Inc. All rights reserved.
 *
 * Use of this source code is governed by the Live2D Open Software license
 * that can be found at https://www.live2d.com/eul      model.update();
      model.draw(projection); // 参照渡しなのprojectionは変質する。

      ite.preIncrement();
    }
  }ve2d-open-software-license-agreement_en.html.
 */

import { CubismMatrix44 } from '@framework/math/cubismmatrix44';
import { ACubismMotion } from '@framework/motion/acubismmotion';
import { csmVector } from '@framework/type/csmvector';
import { csmMap } from '@framework/type/csmmap';

import * as LAppDefine from './lappdefine';
import { canvas } from './lappglmanager';
import { LAppModel, LoadStep } from './lappmodel';
import { LAppPal } from './lapppal';

export let s_instance: LAppLive2DManager = null;

/**
 * サンプルアプリケーションにおいてCubismModelを管理するクラス
 * モデル生成と破棄、タップイベントの処理、モデル切り替えを行う。
 */
export class LAppLive2DManager {
  /**
   * クラスのインスタンス（シングルトン）を返す。
   * インスタンスが生成されていない場合は内部でインスタンスを生成する。
   *
   * @return クラスのインスタンス
   */
  public static getInstance(): LAppLive2DManager {
    if (s_instance == null) {
      s_instance = new LAppLive2DManager();
    }

    return s_instance;
  }

  /**
   * クラスのインスタンス（シングルトン）を解放する。
   */
  public static releaseInstance(): void {
    if (s_instance != null) {
      s_instance = void 0;
    }

    s_instance = null;
  }

  /**
   * 現在のシーンで保持しているモデルを返す。
   *
   * @param no モデルリストのインデックス値
   * @return モデルのインスタンスを返す。インデックス値が範囲外の場合はNULLを返す。
   * @deprecated Use getModelById() instead for more reliable model access
   */
  public getModel(no: number): LAppModel {
    // For backwards compatibility, return current model if index is 0
    if (no === 0 && this._currentModelId) {
      return this.getModelById(this._currentModelId);
    }
    return null;
  }

  /**
   * モデルIDからモデルを取得
   * @param modelId モデルのID
   * @return モデルのインスタンス。存在しない場合はnull
   */
  public getModelById(modelId: string): LAppModel {
    const it = this._models.getValue(modelId);
    return it !== null ? it : null;
  }

  /**
   * 現在表示中のモデルIDを取得
   * @return 現在のモデルID。モデルがない場合はnull
   */
  public getCurrentModelId(): string {
    return this._currentModelId;
  }

  /**
   * Apply a Y offset to the currently active model's matrix.
   * @param offset Offset value in model coordinates.
   */
  public setModelYOffset(offset: number, durationMs = 0): void {
    this._pendingModelYOffset = offset;
    this._pendingModelYOffsetDuration = durationMs;
    if (!this._currentModelId) {
      return;
    }

    const model = this.getModelById(this._currentModelId);
    if (model && typeof model.setModelYOffset === 'function') {
      model.setModelYOffset(offset, durationMs);
    }
  }

  public setModelXOffset(offset: number, durationMs = 0): void {
    this._pendingModelXOffset = offset;
    this._pendingModelXOffsetDuration = durationMs;
    if (!this._currentModelId) {
      return;
    }

    const model = this.getModelById(this._currentModelId);
    if (model && typeof model.setModelXOffset === 'function') {
      model.setModelXOffset(offset, durationMs);
    }
  }

  /**
   * 現在のシーンで保持しているすべてのモデルを解放する
   */
  public releaseAllModel(): void {
    const ite = this._models.begin();
    while (ite.notEqual(this._models.end())) {
      const model = ite.ptr().second;
      if (model) {
        model.release();
      }
      ite.preIncrement();
    }
    this._models.clear();
    this._currentModelId = null;
  }

  /**
   * 画面をドラッグした時の処理
   *
   * @param x 画面のX座標
   * @param y 画面のY座標
   */
  public onDrag(x: number, y: number): void {
    const ite = this._models.begin();
    while (ite.notEqual(this._models.end())) {
      const model = ite.ptr().second;
      if (model) {
        model.setDragging(x, y);
      }
      ite.preIncrement();
    }
  }

  /**
   * 画面をタップした時の処理
   *
   * @param x 画面のX座標
   * @param y 画面のY座標
   */
  public onTap(x: number, y: number): void {
    if (LAppDefine.DebugLogEnable) {
      LAppPal.printMessage(
        `[APP]tap point: {x: ${x.toFixed(2)} y: ${y.toFixed(2)}}`
      );
    }

    // Only handle tap for current model
    if (this._currentModelId) {
      const model = this.getModelById(this._currentModelId);
      if (model) {
        if (model.hitTest(LAppDefine.HitAreaNameHead, x, y)) {
          if (LAppDefine.DebugLogEnable) {
            LAppPal.printMessage(
              `[APP]hit area: [${LAppDefine.HitAreaNameHead}]`
            );
          }
          console.log(model._expressions);
          model.setRandomExpression();
        } else if (model.hitTest(LAppDefine.HitAreaNameBody, x, y)) {
          if (LAppDefine.DebugLogEnable) {
            LAppPal.printMessage(
              `[APP]hit area: [${LAppDefine.HitAreaNameBody}]`
            );
          }
          model.startRandomMotion(
            LAppDefine.MotionGroupTapBody,
            LAppDefine.PriorityNormal,
            this._finishedMotion
          );
        }
      }
    }
  }

  /**
   * 画面を更新するときの処理
   * モデルの更新処理及び描画処理を行う
   */
  public onUpdate(): void {
    const { width, height } = canvas;

    const ite = this._models.begin();
    while (ite.notEqual(this._models.end())) {
      const projection: CubismMatrix44 = new CubismMatrix44();
      const model: LAppModel = ite.ptr().second;

      if (!model) {
        ite.preIncrement();
        continue;
      }

      if (model.getModel()) {
        if (model.getModel().getCanvasWidth() > 1.0 && width < height) {
          // 横に長いモデルを縦長ウィンドウに表示する際モデルの横サイズでscaleを算出する
          model.getModelMatrix().setWidth(2.0);
          projection.scale(1.0, width / height);
        } else {
          projection.scale(height / width, 1.0);
        }

        // 必要があればここで乗算
        if (this._viewMatrix != null) {
          projection.multiplyByMatrix(this._viewMatrix);
        }
      }

      model.update();
      model.draw(projection); // 参照渡しなのでprojectionは変質する。

      ite.preIncrement();
    }
  }

  /**
   * 次のシーンに切りかえる
   * サンプルアプリケーションではモデルセットの切り替えを行う。
   */
  public nextScene(): void {
    const no: number = (this._sceneIndex + 1) % LAppDefine.ModelDirSize;
    this.changeScene(no);
  }

  /**
   * シーンを切り替える
   * サンプルアプリケーションではモデルセットの切り替えを行う。
   */
  public changeScene(index: number): void {
    this._sceneIndex = index;
    if (LAppDefine.DebugLogEnable) {
      LAppPal.printMessage(`[APP]model index: ${this._sceneIndex}`);
    }

    // ModelDir[]に保持したディレクトリ名から
    // model3.jsonのパスを決定する。
    // ディレクトリ名とmodel3.jsonの名前を一致させておくこと。
    const modelId: string = LAppDefine.ModelDir[index];
    const modelPath: string = LAppDefine.ResourcesPath + modelId + '/';
    let modelJsonName: string = modelId + '.model3.json';

    this.releaseAllModel();
    const model = new LAppModel();
    model.setModelYOffset(this._pendingModelYOffset, 0);
    model.setModelXOffset(this._pendingModelXOffset, 0);
    model.loadAssets(modelPath, modelJsonName);
    this._models.setValue(modelId, model);
    this._currentModelId = modelId;
  }

  /**
   * モデルをIDで切り替える (Flutter から呼び出し可能)
   * メモリ節約のため、古いモデルを完全に解放してから新しいモデルをロード
   * @param modelId モデルのディレクトリ名 (例: 'model032', 'shuangsheng')
   * @returns 成功時はtrue、失敗時はfalse
   */
  public changeModel(modelId: string): boolean {
    console.log(`[changeModel] Attempting to change to model: ${modelId}`);

    // モデルIDがModelDir配列に存在するか確認
    const index = LAppDefine.ModelDir.indexOf(modelId);

    if (index === -1) {
      console.error(`[changeModel] Model ID '${modelId}' not found in ModelDir`);
      console.error(`[changeModel] Available models: ${LAppDefine.ModelDir.join(', ')}`);
      return false;
    }

    if (typeof window.pauseLive2DRendering === 'function') {
      window.pauseLive2DRendering();
    }
    console.log(`[changeModel] Found model '${modelId}' at index ${index}`);

    // 現在表示中のモデルを完全に解放 (GPU/CPUメモリを解放)
    console.log(`[changeModel] Releasing old model to free memory...`);
    this.releaseAllModel();

    // 新しいモデルをロード
    const modelPath: string = LAppDefine.ResourcesPath + modelId + '/';
    const modelJsonName: string = modelId + '.model3.json';

    console.log(`[changeModel] Loading model from: ${modelPath}${modelJsonName}`);

    const model = new LAppModel();
    model.setModelYOffset(this._pendingModelYOffset, 0);
    model.setModelXOffset(this._pendingModelXOffset, 0);
    model.loadAssets(modelPath, modelJsonName);

    // モデルIDでmapに追加
    this._models.setValue(modelId, model);
    this._currentModelId = modelId;
    this._sceneIndex = index;

    // Apply pending wardrobe expressions before showing the model
    this.applyPendingWardrobeExpressions(model);


    console.log(`[changeModel] Model '${modelId}' added to map and set as current. Memory freed, new model loading.`);
    return true;
  }


  /**
   * Reload the currently displayed model with updated resolution textures while keeping the
   * old textures visible until the replacement is ready. Intended for resolution tier changes
   * where the logical model remains the same.
   * @returns Promise that resolves to true when the reload completes (or forces success).
   */
  public async reloadCurrentModelWithResolution(): Promise<boolean> {
    if (this._resolutionReloadPromise) {
      console.warn('[reloadCurrentModelWithResolution] Reload already in progress, returning existing promise.');
      return this._resolutionReloadPromise;
    }

    const currentModelId = this._currentModelId;
    if (!currentModelId) {
      console.warn('[reloadCurrentModelWithResolution] No current model to reload');
      return false;
    }

    const index = LAppDefine.ModelDir.indexOf(currentModelId);
    if (index === -1) {
      console.error(`[reloadCurrentModelWithResolution] Current model "${currentModelId}" not found in ModelDir`);
      return false;
    }

    console.log(`[reloadCurrentModelWithResolution] Starting reload for model: ${currentModelId}`);

    // Pause rendering to freeze canvas on last frame (prevents default wardrobe flash)
    if (typeof window.pauseLive2DRendering === 'function') {
      window.pauseLive2DRendering();
    }

    const modelPath: string = LAppDefine.ResourcesPath + currentModelId + '/';
    const modelJsonName: string = currentModelId + '.model3.json';

    const oldModelId = currentModelId;
    const tempModelId = `${currentModelId}_tierReload_${Date.now()}`;

    const newModel = new LAppModel();
    newModel.setModelYOffset(this._pendingModelYOffset, 0);
    newModel.setModelXOffset(this._pendingModelXOffset, 0);
    newModel.loadAssets(modelPath, modelJsonName);

    this._models.setValue(tempModelId, newModel);

    this._resolutionReloadPromise = new Promise<boolean>((resolve) => {
      let lastReportedState: LoadStep | null = null;
      let timeoutId: number | null = null;

      const finishSwap = (forced = false) => {
        console.log('[reloadCurrentModelWithResolution] Swapping to new model');

        this.applyPendingWardrobeExpressions(newModel);

        let ite = this._models.begin();
        while (ite.notEqual(this._models.end())) {
          if (ite.ptr().first === tempModelId) {
            this._models.erase(ite);
            break;
          }
          ite.preIncrement();
        }

        ite = this._models.begin();
        while (ite.notEqual(this._models.end())) {
          if (ite.ptr().first === oldModelId) {
            const model = ite.ptr().second;
            if (model && model !== newModel) {
              model.release();
              console.log(`[reloadCurrentModelWithResolution] Released old model resources for ${oldModelId}`);
            }
            this._models.erase(ite);
            break;
          }
          ite.preIncrement();
        }

        this._models.setValue(currentModelId, newModel);
        this._sceneIndex = index;
        this._currentModelId = currentModelId;

        window.clearInterval(checkInterval);
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
          timeoutId = null;
        }

        if (forced) {
          console.warn('[reloadCurrentModelWithResolution] Completed with timeout fallback; textures may still be streaming.');
        }


        this._resolutionReloadPromise = null;
        resolve(true);
      };

      const checkInterval = window.setInterval(() => {
        const loadState = newModel.getLoadState();

        if (!newModel.isFullyInitialized()) {
          if (loadState !== lastReportedState) {
            console.log(
              `[reloadCurrentModelWithResolution] Waiting for new textures... (state: ${loadState})`
            );
            lastReportedState = loadState;
          }
          return;
        }

        console.log('[reloadCurrentModelWithResolution] New model fully initialized with updated resolution');
        finishSwap();
      }, 100);

      timeoutId = window.setTimeout(() => {
        console.warn('[reloadCurrentModelWithResolution] Timeout while waiting for textures, forcing swap');
        finishSwap(true);
      }, 10000);
    });

    return this._resolutionReloadPromise;
  }

  /**
   * Wait for model data to be ready (expressions + motions loaded)
   * Similar to Dart's waitForModelDataReady method
   */
  private async waitForModelDataReady(model: LAppModel, timeoutMs: number = 10000): Promise<boolean> {
    const startTime = Date.now();
    const pollInterval = 200; // 200ms polling interval
    const loggedExpressionFailures = new Set<string>();
    let lastExpressionProgressKey = '';
    const pendingExpressionTracker = new Map<string, { firstDetected: number; warned: boolean }>();

    while (Date.now() - startTime < timeoutMs) {
      // Check if CubismModel is loaded
      const cubismModel = model.getModel();
      if (!cubismModel) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        continue;
      }

      // Check expression load status using summary data
      const expressionSummary = model.getExpressionLoadSummary();
      const expectedExpressions = expressionSummary.expectedNames.length;
      const loadedExpressions = expressionSummary.loadedNames.length;

      if (expressionSummary.failed.length > 0) {
        for (const failure of expressionSummary.failed) {
          if (!loggedExpressionFailures.has(failure.name)) {
            console.error(
              `[waitForModelDataReady] Expression "${failure.name}" failed to load from "${failure.filePath}". Reason: ${failure.reason}`
            );
            loggedExpressionFailures.add(failure.name);
          }
        }

        return false;
      }

      if (expectedExpressions > 0 && loadedExpressions < expectedExpressions) {
        const pendingNames = expressionSummary.pendingNames.slice().sort();
        const progressKey = `${loadedExpressions}/${expectedExpressions}:${pendingNames.join(',')}`;
        const now = Date.now();

        if (progressKey !== lastExpressionProgressKey) {
          if (pendingNames.length > 0) {
            console.log(
              `[waitForModelDataReady] Waiting for expressions: ${loadedExpressions}/${expectedExpressions} (pending: ${pendingNames.join(', ')})`
            );
          } else {
            console.log(
              `[waitForModelDataReady] Waiting for expressions: ${loadedExpressions}/${expectedExpressions}`
            );
          }
          lastExpressionProgressKey = progressKey;
        }

        // Record and warn about stalled expressions so developers can fix missing files quickly.
        const pendingSet = new Set(pendingNames);
        for (const detail of expressionSummary.pendingDetails) {
          const record = pendingExpressionTracker.get(detail.name);
          if (!pendingSet.has(detail.name)) {
            continue;
          }

          if (record) {
            if (!record.warned && now - record.firstDetected > 2000) {
              const elapsedSinceDetected = now - record.firstDetected;
              const fileInfo = detail.filePath ?? 'unknown path';
              console.warn(
                `[waitForModelDataReady] Expression "${detail.name}" still pending after ${elapsedSinceDetected}ms. Expected file: ${fileInfo}. This often indicates the asset is missing, misnamed, or not copied to the web demo.`
              );
              record.warned = true;
            }
          } else {
            pendingExpressionTracker.set(detail.name, {
              firstDetected: now,
              warned: false
            });
          }
        }

        // Clean up tracker entries for expressions that finished loading
        for (const trackedName of Array.from(pendingExpressionTracker.keys())) {
          if (!pendingSet.has(trackedName)) {
            pendingExpressionTracker.delete(trackedName);
          }
        }

        await new Promise(resolve => setTimeout(resolve, pollInterval));
        continue;
      }

      // Check if motions are loaded
      let motionsLoaded = true;
      if (model._motions) {
        const motionGroups = model._modelSetting ? model._modelSetting.getMotionGroupCount() : 0;
        if (motionGroups > 0 && model._motions.getSize() === 0) {
          console.log(`[waitForModelDataReady] Waiting for motions to load...`);
          motionsLoaded = false;
        }
      }

      if (!motionsLoaded) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        continue;
      }

      // All data ready!
      const elapsed = Date.now() - startTime;
      console.log(`[waitForModelDataReady] Model data ready after ${elapsed}ms`);
      return true;
    }

    // Timeout
    const elapsed = Date.now() - startTime;
    console.warn(`[waitForModelDataReady] Timeout after ${elapsed}ms`);

    const finalSummary = model.getExpressionLoadSummary();
    if (finalSummary.pendingDetails.length > 0) {
      for (const detail of finalSummary.pendingDetails) {
        const fileInfo = detail.filePath ?? 'unknown path';
        console.warn(
          `[waitForModelDataReady] Timed out waiting for expression "${detail.name}". Expected file: ${fileInfo}. Verify the asset exists and the model JSON path is correct.`
        );
      }
    }
    return false;
  }

  private async applyPendingWardrobeExpressions(model: LAppModel): Promise<number> {
    const wardrobeStartTime = performance.now();
    const pending = (window as any).__Live2DPendingWardrobeExpressions;

    if (!pending || typeof pending !== 'object') {
      if (typeof window.resumeLive2DRendering === 'function') {
        window.resumeLive2DRendering();
      }
      return 0;
    }

    // Wait for model data to be ready (expressions + motions loaded)
    const elapsed = Math.round(performance.now() - (window as any).__Live2DLoadStartTime || 0);
    console.log(`[applyPendingWardrobeExpressions] Waiting for model data to be ready... (+${elapsed}ms)`);
    const dataReady = await this.waitForModelDataReady(model, 10000);

    if (!dataReady) {
      const expressionSummary = model.getExpressionLoadSummary();

      if (expressionSummary.failed.length > 0) {
        for (const failure of expressionSummary.failed) {
          console.error(
            `[applyPendingWardrobeExpressions] Skipping wardrobe application because expression "${failure.name}" failed to load from "${failure.filePath}". Reason: ${failure.reason}`
          );
        }
      } else {
        console.warn('[applyPendingWardrobeExpressions] Model data not ready after timeout, skipping wardrobe application');
      }

      if (typeof window.resumeLive2DRendering === 'function') {
        window.resumeLive2DRendering();
      }
      return 0;
    }

    const readyElapsed = Math.round(performance.now() - (window as any).__Live2DLoadStartTime || 0);
    console.log(`[applyPendingWardrobeExpressions] Model data ready, applying wardrobe... (+${readyElapsed}ms)`);

    let appliedChannels = 0;

    for (const [channel, rawValue] of Object.entries(pending)) {
      let expressions: string[] = [];

      if (Array.isArray(rawValue)) {
        expressions = rawValue
          .map((expr) => (typeof expr === 'string' ? expr.trim() : ''))
          .filter((expr) => expr.length > 0);
      } else if (typeof rawValue === 'string') {
        expressions = rawValue
          .split('+')
          .map((expr) => expr.trim())
          .filter((expr) => expr.length > 0);
      }

      if (expressions.length === 0) {
        continue;
      }

      try {
        if (expressions.length === 1) {
          model.playExpressionInstant(channel, expressions[0]);
        } else {
          model.playMultipleExpressions(channel, expressions, true);
        }

        appliedChannels++;
      } catch (error) {
        console.error(
          `[reloadCurrentModelWithResolution] Failed to pre-apply expressions for channel "${channel}":`,
          error
        );
      }
    }

    if (appliedChannels > 0) {
      const totalElapsed = Math.round(performance.now() - (window as any).__Live2DLoadStartTime || 0);
      const wardrobeDuration = Math.round(performance.now() - wardrobeStartTime);
      console.log(
        `[reloadCurrentModelWithResolution] Applied pending wardrobe expressions to ${appliedChannels} channel${appliedChannels === 1 ? '' : 's'} in ${wardrobeDuration}ms (+${totalElapsed}ms total)`
      );
    }

    (window as any).__Live2DPendingWardrobeExpressions = null;


    if (typeof window.resumeLive2DRendering === 'function') {
      window.resumeLive2DRendering();
    }
    return appliedChannels;
  }

  public setViewMatrix(m: CubismMatrix44) {
    for (let i = 0; i < 16; i++) {
      this._viewMatrix.getArray()[i] = m.getArray()[i];
    }
  }

  /**
   * 現在表示中のモデル数を取得
   * @returns 表示中のモデル数
   */
  public getModelCount(): number {
    return this._models.getSize();
  }

  /**
   * コンストラクタ
   */
  constructor() {
    this._viewMatrix = new CubismMatrix44();
    this._models = new csmMap<string, LAppModel>();
    this._currentModelId = null;
    this._sceneIndex = 0;
    this._pendingModelYOffset = LAppDefine.ModelInitialYOffset;
    this._pendingModelYOffsetDuration = 0;
    this._pendingModelXOffset = 0;
    this._pendingModelXOffsetDuration = 0;
    // Don't load any model by default - wait for Flutter to call changeModel()
    console.log('[LAppLive2DManager] Initialized without loading default model');
  }

  _viewMatrix: CubismMatrix44; // モデル描画に用いるview行列
  _models: csmMap<string, LAppModel>; // モデルインスタンスのコンテナ (ModelID -> Model)
  _currentModelId: string; // 現在表示中のモデルID
  _sceneIndex: number; // 表示するシーンのインデックス値
  private _resolutionReloadPromise: Promise<boolean> | null = null;
  private _pendingModelYOffset: number;
  private _pendingModelYOffsetDuration: number;
  private _pendingModelXOffset: number;
  private _pendingModelXOffsetDuration: number;
  // モーション再生終了のコールバック関数
  _finishedMotion = (self: ACubismMotion): void => {
    LAppPal.printMessage('Motion Finished:');
    console.log(self);
  };
}
